import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { ExpandLess, ExpandMore, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  InputAdornment,
  MenuItem,
  Typography,
} from "@mui/material";
import _ from "lodash";
import { ReactElement, useState } from "react";
import { useController, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";

import {
  CampaignStatus,
  ListSubaccountEscalationInboxesQuery,
  ListSubaccountEscalationInboxesQueryVariables,
  MessagingStrategyName,
  PhoneNumberMessageClass,
  ThreadResponseStatus,
} from "~frontend/__generated__/graphql";
import { ControlsPaper, ControlsRow } from "~frontend/components/ControlsPaper";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import {
  MessageClassDisplayName,
  useMessageClassDisplayName,
} from "~frontend/components/MessageClassDisplayName";
import { MultiSelectValue } from "~frontend/components/forms/FormSelect";
import {
  RhfSelect,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import CheckBoxUnchecked from "~frontend/components/icons/CheckBoxUnchecked";
import { LIST_SUBACCOUNT_ESCALATION_INBOXES } from "~frontend/graphql/inboxes";
import { useSubaccountSupervolunteerConfig } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";

import { useThreadsContext } from "../../../../components/threads/ThreadsProvider";
import {
  CAMPAIGN_STATUS_FILTERS,
  OPT_OUT_FILTERS,
  THREAD_SORTS,
} from "../../../../components/threads/constants";
import { ThreadResponseStatusLabel } from "../ThreadResponseStatusLabel";
import { InboxFilterDialog } from "./InboxFilterDialog";
import { RhfCampaignsAndTextersField } from "./RhfCampaignsAndTextersField";
import { TagFilterDialog } from "./TagFilterDialog";
import {
  ThreadFilterAndSortValues,
  ThreadFiltersAndSortSchema,
} from "./schema";

const MESSAGE_CLASSES_FOR_MESSAGING_STRATEGY: {
  [key in MessagingStrategyName]: PhoneNumberMessageClass[];
} = {
  [MessagingStrategyName.BandwidthShortCode]: [
    PhoneNumberMessageClass.ShortCode,
  ],
  [MessagingStrategyName.BandwidthTollFree]: [PhoneNumberMessageClass.TollFree],
  [MessagingStrategyName.BandwidthUnregisteredPool]: [
    PhoneNumberMessageClass.Unregistered,
  ],
  [MessagingStrategyName.Bandwidth_10Dlc]: [PhoneNumberMessageClass.TenDlc],
  [MessagingStrategyName.Bandwidth_10DlcPool]: [PhoneNumberMessageClass.TenDlc],
  [MessagingStrategyName.Fake]: [
    PhoneNumberMessageClass.Unregistered,
    PhoneNumberMessageClass.ShortCode,
  ],
  [MessagingStrategyName.FakePool]: [PhoneNumberMessageClass.TenDlc],
  [MessagingStrategyName.FakeShortCode]: [PhoneNumberMessageClass.ShortCode],
  [MessagingStrategyName.TwilioShortCode]: [PhoneNumberMessageClass.ShortCode],
  [MessagingStrategyName.Sinch_10Dlc]: [PhoneNumberMessageClass.TenDlc],
  [MessagingStrategyName.FakeTollFree]: [PhoneNumberMessageClass.TollFree],
  [MessagingStrategyName.Fake_10Dlc]: [PhoneNumberMessageClass.TenDlc],
};

export default function ThreadListControls(): ReactElement {
  const subaccount = useSubaccountSupervolunteerConfig()!;

  const [controlsOpen, setControlsOpen] = useState(true);

  const { listThreadsFilterAndSort, setListThreadFilterAndSort, threadsList } =
    useThreadsContext()!;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(ThreadFiltersAndSortSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    values: {
      campaignsAndTexters: [
        ...listThreadsFilterAndSort.campaignFilter.map(campaign => ({
          ...campaign,
          type: "campaign" as const,
        })),
        ...listThreadsFilterAndSort.texterFilter.map(texter => ({
          ...texter,
          type: "texter" as const,
        })),
      ],
      tags: listThreadsFilterAndSort.tagFilter.tags,
      tagFilterOperation: listThreadsFilterAndSort.tagFilter.filterOperation,
      searchType: listThreadsFilterAndSort.searchType,
      search: listThreadsFilterAndSort.search,
      isOptedOut: listThreadsFilterAndSort.optOutFilter,
      responseStatus:
        listThreadsFilterAndSort.responseStatusFilter ||
        ("" as ThreadResponseStatus | ""),
      phoneNumberMessageClasses:
        listThreadsFilterAndSort.phoneNumberMessageClassesFilter,
      inboxes: listThreadsFilterAndSort.inboxes,
      inboxFilterType: listThreadsFilterAndSort.inboxFilterType,
      campaignStatuses: listThreadsFilterAndSort.campaignStatuses,
      sortBy: listThreadsFilterAndSort.sort,
    },
  });

  const { field: campaignStatusesField } = useController({
    control,
    name: "campaignStatuses",
  });
  const { field: phoneNumberMessageClassesField } = useController({
    control,
    name: "phoneNumberMessageClasses",
  });

  const [showTagFilterModal, setShowTagFilterModal] = useState(false);
  const [showEscalationInboxFilterModal, setShowEscalationInboxFilterModal] =
    useState(false);

  const { data: escalationInboxData } = useQuery<
    ListSubaccountEscalationInboxesQuery,
    ListSubaccountEscalationInboxesQueryVariables
  >(LIST_SUBACCOUNT_ESCALATION_INBOXES, {
    variables: { input: { subaccountId: subaccount.id } },
  });

  const onSubmit = ({
    campaignsAndTexters,
    searchType,
    search,
    isOptedOut,
    responseStatus,
    phoneNumberMessageClasses,
    sortBy,
    tags,
    tagFilterOperation,
    inboxes,
    inboxFilterType,
    campaignStatuses,
  }: ThreadFilterAndSortValues) =>
    setListThreadFilterAndSort({
      campaignFilter: campaignsAndTexters
        .filter(option => option.type === "campaign")
        .map(option => _.pick(option, "id", "name")),
      texterFilter: campaignsAndTexters
        .filter(option => option.type === "texter")
        .map(option => _.pick(option, "id", "name")),
      optOutFilter: isOptedOut,
      responseStatusFilter: responseStatus === "" ? null : responseStatus,
      phoneNumberMessageClassesFilter: phoneNumberMessageClasses,
      sort: sortBy,
      searchType,
      search,
      tagFilter: {
        tags,
        filterOperation: tagFilterOperation,
      },
      inboxes,
      inboxFilterType,
      campaignStatuses,
    });

  const messageClassDisplayNames = useMessageClassDisplayName();

  const messagingStrategies = subaccount.messagingStrategies.filter(
    s => s.isConfigured && s.isEnabled
  );

  const phoneNumberMessageClasses = [
    ...new Set(
      messagingStrategies.flatMap(
        messagingStrategy =>
          MESSAGE_CLASSES_FOR_MESSAGING_STRATEGY[messagingStrategy.name]
      )
    ),
  ];

  if (!subaccount) {
    return <Loading />;
  }

  return (
    <>
      <Collapse in={controlsOpen}>
        <ControlsPaper
          elevation={0}
          sx={{
            background: "none",
            marginBottom: 0,
            borderBottom: `1px solid ${palette.midGray}`,
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <ControlsRow sx={{ gap: "40px" }}>
              <FlexBox gap="40px" sx={{ width: "100%" }}>
                <RhfSelect
                  control={control}
                  name="sortBy"
                  secondary
                  label={
                    <FormattedMessage
                      defaultMessage="Sort By"
                      description="Label for sort dropdown"
                    />
                  }
                  fullWidth
                >
                  {THREAD_SORTS.map(({ value, label }) => (
                    <MenuItem value={value} key={value}>
                      {label}
                    </MenuItem>
                  ))}
                </RhfSelect>
                <RhfSelect
                  control={control}
                  name="responseStatus"
                  secondary
                  label={
                    <FormattedMessage
                      defaultMessage="Response Status"
                      description="Label for thread list filter on response status"
                    />
                  }
                  fullWidth
                >
                  <MenuItem
                    key="NONE"
                    value=""
                    sx={{ color: palette.darkGray }}
                  >
                    <FormattedMessage
                      defaultMessage="Any Status"
                      description="The any option for the response status select"
                    />
                  </MenuItem>
                  {[
                    ThreadResponseStatus.HasRepliedNeedsResponse,
                    ThreadResponseStatus.HasRepliedDoesntNeedResponse,
                    ThreadResponseStatus.NoReply,
                  ].map(responseStatus => (
                    <MenuItem key={responseStatus} value={responseStatus}>
                      <ThreadResponseStatusLabel
                        responseStatus={responseStatus}
                      />
                    </MenuItem>
                  ))}
                </RhfSelect>
                {phoneNumberMessageClasses.length > 1 ? (
                  <RhfSelect
                    control={control}
                    name="phoneNumberMessageClasses"
                    secondary
                    label={
                      <FormattedMessage
                        defaultMessage="Message Classes"
                        description="Label for messaging class filter"
                      />
                    }
                    fullWidth
                    renderValue={value =>
                      (value as PhoneNumberMessageClass[])
                        .map(messageClass =>
                          messageClassDisplayNames(messageClass)
                        )
                        .join(", ")
                    }
                    MenuProps={{
                      sx: {
                        "& .Mui-selected": {
                          backgroundColor: palette.lightGray,
                          "&.Mui-focused": {
                            backgroundColor: palette.lightGray,
                          },
                        },
                      },
                    }}
                    multiple
                  >
                    {phoneNumberMessageClasses.map(messageClass => (
                      <MenuItem value={messageClass} key={messageClass}>
                        <Checkbox
                          checked={phoneNumberMessageClassesField.value.includes(
                            messageClass
                          )}
                          icon={<CheckBoxUnchecked />}
                        />
                        <MessageClassDisplayName messageClass={messageClass} />
                      </MenuItem>
                    ))}
                  </RhfSelect>
                ) : null}
                <RhfSelect
                  control={control}
                  name="campaignStatuses"
                  multiple
                  secondary
                  label={
                    <FormattedMessage
                      defaultMessage="Campaign Status"
                      description="Label for campaign status dropdown"
                    />
                  }
                  renderValue={value => (
                    <MultiSelectValue
                      selected={value as CampaignStatus[]}
                      options={CAMPAIGN_STATUS_FILTERS.map(csf => ({
                        value: csf.value,
                        label: csf.optionLabel,
                      }))}
                    />
                  )}
                  fullWidth
                >
                  {CAMPAIGN_STATUS_FILTERS.map(({ value, optionLabel }) => (
                    <MenuItem value={value} key={value}>
                      <Checkbox
                        checked={campaignStatusesField.value.includes(value)}
                        icon={<CheckBoxUnchecked />}
                      />
                      {optionLabel}
                    </MenuItem>
                  ))}
                </RhfSelect>
                <RhfSelect
                  control={control}
                  name="isOptedOut"
                  secondary
                  label={
                    <FormattedMessage
                      defaultMessage="Opt Out Status"
                      description="Label for opt-out status dropdown"
                    />
                  }
                  fullWidth
                >
                  {OPT_OUT_FILTERS.map(({ value, label }) => (
                    <MenuItem value={value} key={value}>
                      {label}
                    </MenuItem>
                  ))}
                </RhfSelect>
              </FlexBox>
              <FlexBox
                direction="column"
                justifyContent="space-between"
                gap="8px"
                sx={{ minWidth: "120px" }}
              >
                <LinkButton onClick={() => setShowTagFilterModal(true)}>
                  <Typography
                    sx={{ textAlign: "center" }}
                    variant={
                      listThreadsFilterAndSort.tagFilter.tags.length > 0
                        ? "body2Bold"
                        : "body2"
                    }
                  >
                    <FormattedMessage
                      description="Link to open the tag filter modal"
                      defaultMessage="Filter by Tag"
                    />
                    {listThreadsFilterAndSort.tagFilter.tags.length ? (
                      <> ({listThreadsFilterAndSort.tagFilter.tags.length})</>
                    ) : null}
                  </Typography>
                </LinkButton>
                {escalationInboxData?.listSubaccountEscalationInboxes.length ? (
                  <LinkButton
                    onClick={() => setShowEscalationInboxFilterModal(true)}
                  >
                    <Typography
                      sx={{ textAlign: "center" }}
                      variant={
                        listThreadsFilterAndSort.inboxes.length > 0
                          ? "body2Bold"
                          : "body2"
                      }
                    >
                      <FormattedMessage
                        description="Link to open the inbox filter modal"
                        defaultMessage="Filter by Inbox"
                      />
                      {listThreadsFilterAndSort.inboxes.length ? (
                        <> ({listThreadsFilterAndSort.inboxes.length})</>
                      ) : null}
                    </Typography>
                  </LinkButton>
                ) : null}
              </FlexBox>
            </ControlsRow>
            <ControlsRow sx={{ gap: "40px", marginTop: "12px" }}>
              <RhfCampaignsAndTextersField
                control={control}
                name="campaignsAndTexters"
              />
              <Box sx={{ width: "50%", gap: 0 }}>
                <RhfSelect
                  name="searchType"
                  control={control}
                  label={
                    <Typography variant="body1Bold" sx={{ fontWeight: 600 }}>
                      <FormattedMessage
                        defaultMessage="Search by"
                        description="Label for search type field"
                      />
                    </Typography>
                  }
                  InputProps={{
                    sx: { fontSize: "12px" },
                  }}
                  sx={{ width: "110px" }}
                >
                  <MenuItem key="message" value="message">
                    <FormattedMessage
                      defaultMessage="Message"
                      description="Label for message search option"
                    />
                  </MenuItem>
                  <MenuItem key="contact" value="contact">
                    <FormattedMessage
                      defaultMessage="Contact"
                      description="Label for contact search option"
                    />
                  </MenuItem>
                </RhfSelect>
                <RhfTextField
                  name="search"
                  control={control}
                  error={!!errors.search}
                  helperText={errors.search?.message}
                  label={
                    <Typography variant="body1Bold" sx={{ fontWeight: 600 }}>
                      <FormattedMessage
                        defaultMessage="Search"
                        description="Label for text search field"
                      />
                    </Typography>
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={{ padding: theme.spacing(1) }}
                      >
                        <Search />
                      </InputAdornment>
                    ),
                    sx: {
                      fontSize: "12px",
                    },
                  }}
                  sx={{
                    width: "calc(100% - 109px)",
                    marginLeft: "-1px",
                    ".MuiFormHelperText-root": {
                      // Prevent vertical expansion of form when error text appears
                      position: "absolute",
                      top: "64px",
                    },
                  }}
                />
              </Box>

              <Button
                variant="contained"
                sx={{ marginTop: "24px" }}
                color="primary"
                type="submit"
                disabled={
                  threadsList.loading ||
                  !isDirty ||
                  !!Object.keys(errors).length
                }
              >
                <FormattedMessage
                  defaultMessage="Apply Filters"
                  description="Button label to apply thread filters"
                />
              </Button>
            </ControlsRow>
            <TagFilterDialog
              open={showTagFilterModal}
              control={control}
              onApply={handleSubmit(onSubmit)}
              subaccountId={subaccount.id}
              onClose={() => setShowTagFilterModal(false)}
            />
            <InboxFilterDialog
              control={control}
              open={showEscalationInboxFilterModal}
              onApply={handleSubmit(onSubmit)}
              onClose={() => setShowEscalationInboxFilterModal(false)}
            />
          </form>
        </ControlsPaper>
      </Collapse>
      <FlexBox justifyContent={"flex-end"} sx={{ height: 0 }}>
        <Button
          onClick={() => setControlsOpen(!controlsOpen)}
          sx={{
            zIndex: 50,
            marginRight: "8px",
            width: controlsOpen ? "40px" : "110px",
            minWidth: 0,
            height: "20px",
            color: palette.darkGray,
            backgroundColor: palette.lightestGray,
            transition: "all 0.25s",

            "&:hover": {
              backgroundColor: palette.lightGray,
              width: "110px",
            },
          }}
        >
          {controlsOpen ? <ExpandLess /> : <ExpandMore />}
        </Button>
      </FlexBox>
    </>
  );
}
